import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';


import { Treatment } from '@next/arable-lib/advice';
import { CropField } from '@next/arable-lib/crop-field';
import { filterLoaded } from '@next/arable-lib/external-resource';
import { AbstractDialog, AbstractDialogResultData, AbstractFormDialog, FormDialogComponent } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { loadSoilTypesInitiate, SoilTypeSelectors } from 'src/app/soil-type';

import { CropFieldDialogModel } from './cropfield-dialog.model';
import { selectIsAutomaticAdviceCustomer } from '../../store/selectors';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { CropFieldTreatmentsComponent } from '../cropfield-treatments/cropfield-treatments.component';
import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { CropVarietyService } from 'src/app/shared/services/crop-variety.service';
@Component({
  selector: 'app-cropfield-dialog',
  templateUrl: './cropfield-dialog.component.html',
  styleUrls: ['cropfield-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    FormsModule,
    ReactiveFormsModule,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatDivider,
    MatIcon,
    CropFieldTreatmentsComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class CropFieldDialogComponent extends AbstractFormDialog<CropField> implements OnInit, OnDestroy {
  data = inject<CropFieldDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<AbstractDialog<CropField>, AbstractDialogResultData<CropField>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  formGroup = this.fb.group({ cropField: this.data.cropField });
  cropVariety = signal<string | undefined>(undefined);
  soilType = signal<string | undefined>(undefined);
  isAutomaticAdviceCustomer$ = this.store.select(selectIsAutomaticAdviceCustomer);
  protected destroy$ = new Subject<boolean>();
  locale = signal<string | undefined>(undefined);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  get crop(): string {
    return this.data?.crop?.agrifirmCrop?.name;
  }

  get cropField(): CropField {
    return this.formGroup.get('cropField')?.value as CropField;
  }

  get gbmTreatments(): Treatment[] {
    return this.data.gbmTreatments ?? [];
  }

  get fertilizationTreatments(): Treatment[] {
    return this.data.fertilizationTreatments ?? [];
  }

  get isAutomaticFertilizationAdvice(): boolean {
    return this.data.isAutomaticFertilizationAdvice ?? false;
  }

  get showFertilizationAdvice$() {
    return this.isAutomaticAdviceCustomer$.pipe(
      map((isAutomatic) => !isAutomatic || !this.isAutomaticFertilizationAdvice),
    );
  }

  ngOnInit(): void {
    this.locale.set(this.localeService.getLocale());
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale.set(this.localeService.getLocale());
    });

    this.store.dispatch(loadSoilTypesInitiate())

    this.store.select(SoilTypeSelectors.select)
      .pipe(takeUntil(this.destroyed$), filterLoaded())
      .subscribe((soilTypes) => {
        this.soilType.set(soilTypes.find(
          (soilType) => soilType.id === this.data.cropField.soilTypeId,
        )?.name);
      });

    this.cropVarietyService
      .getCropVarietiesByCropIds([this.data.cropField.cropId])
      .subscribe((cropVarieties) => {
        this.cropVariety.set(cropVarieties.find(
          (variety) => variety.id === this.data.cropField.cropVarietyId,
        )?.name);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
